import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet, Font } from '@react-pdf/renderer';

interface CertificatePDFProps {
  tournament: string;
  duration: string;
  qrCodeUrl: string;
  mainTitle: string;
  heroSubtitle: string;
  heroMessage: string;
  yourReward: string;
  prizeDescription: string;
  tagline: string;
  yourCode: string;
  scanInstruction: string;
  prizePoweredBy: string;
  monthsText: string;
  worthText: string;
  placeLabel: string;
  place: string;
  membershipText: string;
}

// Register Baloo 2 font (using Google Fonts)
Font.register({
  family: 'Baloo 2',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/baloo2/v23/wXK0E3kTposypRydzVT08TS3JnAmtdgazapv.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://fonts.gstatic.com/s/baloo2/v23/wXK0E3kTposypRydzVT08TS3JnAmtdj9yqpv.ttf',
      fontWeight: 700,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'Baloo 2',
    paddingHorizontal: 40,
    paddingBottom: 40,
    paddingTop: 30,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  // App icon at top
  appIconTopSection: {
    alignItems: 'center',
    marginBottom: 12,
  },
  appIconTop: {
    width: 100,
    height: 100,
    borderRadius: 20,
  },
  // Hero section
  heroSection: {
    alignItems: 'center',
    marginBottom: 16,
  },
  mainTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1C7AD6',
    letterSpacing: 1,
    textAlign: 'center',
    marginBottom: 16,
    paddingHorizontal: 30,
  },
  heroSubtitle: {
    fontSize: 13,
    color: '#1e293b',
    textAlign: 'center',
    marginBottom: 14,
    fontWeight: 700,
  },
  heroMessage: {
    fontSize: 11,
    color: '#64748b',
    textAlign: 'center',
    lineHeight: 1.7,
    paddingHorizontal: 40,
  },
  // Divider
  divider: {
    width: '25%',
    height: 1.5,
    backgroundColor: '#e2e8f0',
    marginVertical: 24,
    alignSelf: 'center',
  },
  // Reward section
  rewardSection: {
    alignItems: 'center',
    marginBottom: 12,
  },
  yourRewardTitle: {
    fontSize: 11,
    color: '#94a3b8',
    textAlign: 'center',
    marginBottom: 8,
    letterSpacing: 0.5,
  },
  prizeBox: {
    backgroundColor: '#ffffff',
    border: '3px solid #1C7AD6',
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    width: '70%',
    alignItems: 'center',
  },
  trophyContainer: {
    marginBottom: 0,
    marginTop: -6,
  },
  trophyInBox: {
    width: 110,
    height: 110,
  },
  durationText: {
    fontSize: 28,
    fontWeight: 700,
    color: '#ff9f10',
    marginBottom: 0,
    marginTop: 2,
  },
  // Membership badge (yellow background, white text)
  membershipBadge: {
    backgroundColor: '#ff9f10',
    borderRadius: 20,
    paddingVertical: 6,
    paddingHorizontal: 16,
    marginTop: 2,
    marginBottom: 0,
    alignItems: 'center',
  },
  membershipText: {
    fontSize: 11,
    color: '#ffffff',
    fontWeight: 700,
    textAlign: 'center',
  },
  // Worth text (plain gray text)
  worthTextPlain: {
    fontSize: 10,
    color: '#64748b',
    textAlign: 'center',
    marginTop: 2,
  },
  prizeDescription: {
    fontSize: 10,
    color: '#64748b',
    textAlign: 'center',
    paddingHorizontal: 50,
    lineHeight: 1.5,
  },
  // Tagline section
  taglineSection: {
    alignItems: 'center',
    marginBottom: 24,
  },
  tagline: {
    fontSize: 9.5,
    color: '#94a3b8',
    textAlign: 'center',
    lineHeight: 1.7,
  },
  // Bottom row with tournament, signature, and founder
  bottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginTop: 8,
  },
  // Tournament section
  tournamentSection: {
    alignItems: 'flex-start',
    flex: 1,
  },
  // Shared info styles
  infoLabel: {
    fontSize: 8,
    color: '#94a3b8',
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 11,
    color: '#1e293b',
    fontWeight: 700,
  },
  // QR Code section inside prize box
  qrInBoxSection: {
    alignItems: 'center',
    marginTop: 10,
    paddingTop: 10,
    borderTop: '1px solid #e2e8f0',
    width: '100%',
  },
  yourCodeLabel: {
    fontSize: 10,
    color: '#64748b',
    marginBottom: 10,
    textAlign: 'center',
  },
  qrBox: {
    backgroundColor: '#ffffff',
    border: '2px solid #e2e8f0',
    padding: 10,
    borderRadius: 12,
    marginBottom: 8,
  },
  qrCode: {
    width: 100,
    height: 100,
  },
  scanInstruction: {
    fontSize: 8,
    color: '#64748b',
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  // Signature section (center)
  signatureSection: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
    paddingTop: 4,
  },
  signatureImage: {
    width: 80,
    height: 40,
  },
  // Founder section (right)
  founderSection: {
    alignItems: 'flex-end',
    flex: 1,
  },
  // Mockup at bottom
  mockupContainer: {
    position: 'absolute',
    bottom: -140,
    left: 0,
    right: 0,
    alignItems: 'center',
    overflow: 'hidden',
  },
  mockupImage: {
    width: 340,
    height: 340,
    objectFit: 'contain',
  },
});

const CertificatePDF: React.FC<CertificatePDFProps> = ({
  tournament,
  duration,
  qrCodeUrl,
  mainTitle,
  heroSubtitle,
  heroMessage,
  yourReward,
  prizeDescription,
  tagline,
  yourCode,
  scanInstruction,
  prizePoweredBy,
  monthsText,
  worthText,
  placeLabel,
  place,
  membershipText,
}) => {
  const trophyUrl = `${window.location.origin}/trophy.png`;
  const appIconUrl = `${window.location.origin}/app-icon.png`;
  const signatureUrl = `${window.location.origin}/doruk-signature.png`;
  const worthValue = parseInt(duration) * 5;

  return (
    <Document>
      <Page size="A4" orientation="portrait" style={styles.page}>
        {/* App Icon */}
        <View style={styles.appIconTopSection}>
          <Image src={appIconUrl} style={styles.appIconTop} />
        </View>

        {/* Hero Section */}
        <View style={styles.heroSection}>
          <Text style={styles.mainTitle}>{mainTitle}</Text>
          <Text style={styles.heroSubtitle}>{heroSubtitle}</Text>
          <Text style={styles.heroMessage}>{heroMessage}</Text>
        </View>

        {/* Reward Section */}
        <View style={styles.rewardSection}>
          <Text style={styles.yourRewardTitle}>{yourReward}</Text>
          <View style={styles.prizeBox}>
            <View style={styles.trophyContainer}>
              <Image src={trophyUrl} style={styles.trophyInBox} />
            </View>
            <Text style={styles.durationText}>{duration} {monthsText}</Text>

            {/* Membership badge */}
            <View style={styles.membershipBadge}>
              <Text style={styles.membershipText}>TTStats Pro {membershipText}</Text>
            </View>

            {/* Worth text */}
            <Text style={styles.worthTextPlain}>{worthText} CHF {worthValue}.-</Text>

            {/* QR Code inside prize box */}
            <View style={styles.qrInBoxSection}>
              <Text style={styles.yourCodeLabel}>{yourCode}</Text>
              <View style={styles.qrBox}>
                <Image src={qrCodeUrl} style={styles.qrCode} />
              </View>
              <Text style={styles.scanInstruction}>{scanInstruction}</Text>
            </View>
          </View>
        </View>

        {/* Tournament, Signature & Founder Row */}
        <View style={styles.bottomRow}>
          {/* Tournament Section */}
          <View style={styles.tournamentSection}>
            <Text style={styles.infoLabel}>{prizePoweredBy}</Text>
            <Text style={styles.infoValue}>{tournament}</Text>
          </View>

          {/* Signature Section (Center) */}
          <View style={styles.signatureSection}>
            <Image src={signatureUrl} style={styles.signatureImage} />
          </View>

          {/* Place Section */}
          <View style={styles.founderSection}>
            <Text style={styles.infoLabel}>{placeLabel}</Text>
            <Text style={styles.infoValue}>{place}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default CertificatePDF;
